import { NPIStatus } from "@app/projects/enumerators";
import { JSX, RefObject } from "react";
import { Helmet } from "react-helmet";

/**
 * Determines whether all keys in an object are considered valid (from a configuration standpoint).
 * This is true if they're NOT undefined.
 * Terminates early and returns false on the first undefined value.
 * @param obj The object to check.
 */
function allObjectKeysValid<T>(obj: T): boolean {
  return !Object.keys(obj)
    .some((key) => (obj)[key as keyof T] === undefined);
}

/**
 * Returns a particular value corresponding to the state of a nullable boolean value.
 * @param value The value being checked.
 * @param trueVal The output value if the value is true.
 * @param falseVal The output value if the value is false.
 * @param nullVal The output value if the value is null.
 */
function fromNullable<T>(
  value: boolean | null,
  trueVal: T,
  falseVal: T,
  nullVal: T): T {
  if (value === true) {
    return trueVal;
  } else if (value === false) {
    return falseVal;
  } else {
    return nullVal;
  }
}

// retrieves the width of vertical scroll bars/the height of horizontal scroll bars
// the result varies by browser and/or operating system
function getScrollBarWidth(): number {
  const inner = document.createElement("p");
  inner.style.width = "100%";
  inner.style.height = "200px";

  const outer = document.createElement("div");
  outer.style.position = "absolute";
  outer.style.top = "0px";
  outer.style.left = "0px";
  outer.style.visibility = "hidden";
  outer.style.width = "200px";
  outer.style.height = "150px";
  outer.style.overflow = "hidden";
  outer.appendChild(inner);

  document.body.appendChild(outer);
  const w1 = inner.offsetWidth;
  outer.style.overflow = "scroll";
  let w2 = inner.offsetWidth;

  if (w1 === w2) {
    w2 = outer.clientWidth;
  }

  document.body.removeChild(outer);

  return (w1 - w2);
}

function sortStringListOrderAscending(
  stringOne: string,
  stringTwo: string): number {
  if (stringOne > stringTwo) {
    return 1;
  } else if (stringOne < stringTwo) {
    return -1;
  }
  return 0;
}

/**
 * In the event that an API returns the property names in a malformed or undesired fashion, this method can update according to a matching expression.
 *
 * Ex:
 * const original = { UID: "1234-ABCD", nested: { UID: "5678-EFGH" } }
 *
 * updateFieldNames(original, /UID/, "uid")
 *   => { uid: "1234-ABCD", nested: { uid: "5678-EFGH" } }
 *
 * @param obj The object to update.
 * @param matchingRegex The regular expression to match with.
 * @param replacement The replacement property name.
 */
function updateFieldNames<T>(obj: T, matchingRegex: RegExp, replacement: string): T {
  for (const key of Object.keys(obj)) {
    if (typeof (obj)[key as keyof T] === "object") {
      (obj)[key as keyof T] = updateFieldNames((obj)[key as keyof T], matchingRegex, replacement);
    }

    if (matchingRegex.test(key)) {
      const value = (obj)[key as keyof T];
      delete (obj)[key as keyof T];
      (obj)[replacement as keyof T] = value;
    }
  }

  return obj;
}

/**
 * This prevents the default mouse event action from happening.
 * This is useful when using anchor tags without other click handlers, names, or real linking hrefs.
 * It can be used to prevent the page from scrolling to top when supplying a '#' as the href for an anchor tag,
 * as javacript:void(0) is frowned upon by future versions of React.
 * @param e The event to block the default action on
 * @param callback A function to execute after preventing the default event action
 */
function preventDefaultMouseEvent(e: React.MouseEvent, callback?: () => void): void {
  e.preventDefault();

  if (callback) {
    callback();
  }
}

function isNullOrUndefined<T>(obj: T): boolean {
  if (obj === null || obj === undefined) {
    return true;
  }
}

function capitalizeFirstLetter(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

function truncateStringWithEllipsis(text: string, length: number): string {
  if (!text) {
    return '';
  }
  if (text.length <= length) {
    return text;
  }
  return `${text.slice(0, length)}...`;
}

function getComparisonChangeElement(changeNum: number, formatter?: (num: number) => string): JSX.Element {
  let changeDisp = null;
  const changeNumDisp = !!formatter ? formatter(changeNum) : changeNum;
  if (changeNum > 0) {
    changeDisp = (<div className="text-success">{changeNumDisp}<i className="fa fa-arrow-up ml-1 mb-1"></i></div>);
  } else if (changeNum < 0) {
    changeDisp = (<div className="text-danger">{changeNumDisp}<i className="fa fa-arrow-down ml-1 mb-1"></i></div>);
  } else {
    changeDisp = (<div>{changeNumDisp}<i className="fa fa-arrows-h ml-1 mb-1"></i></div>);
  }
  return changeDisp;
}

function valueOrDashes(value: string | NPIStatus | boolean | number, numberAsCurrency?: boolean): React.ReactNode {
  let result: React.ReactNode = value;
  if (typeof result === "boolean") {
    result = result === undefined
      ? null
      : value
        ? "Y"
        : "N";
  }
  if (numberAsCurrency && (typeof result === "number")) {
    result = result
      ? `$${result.toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
      : result;
  }

  return result || (<>---</>);
}

function querystringify(queryObj: { [key: string]: unknown }): string {
  const safeQueryObj = new URLSearchParams();
  Object.keys(queryObj).forEach((key) => {
    if (queryObj[key] !== undefined && queryObj[key] !== null) {
      switch (typeof queryObj[key]) {
        case "number":
        case "boolean":
          safeQueryObj.append(key, encodeURIComponent(queryObj[key] as number | boolean));
          break;
        case "object":
          const keys = Object.keys(queryObj[key]);
          keys.forEach((childKey) => {
            safeQueryObj.append(key, encodeURIComponent((queryObj[key] as { [key: string]: unknown })[childKey] as string | number | boolean));
          })
          break;
        case "string":
        default:
          safeQueryObj.append(key, queryObj[key] as string);
          break;
      }
    }
  });

  return safeQueryObj.toString();
}

function parseQuerystring(querystring: string): { [key: string]: string } {
  const entries = new URLSearchParams(querystring).entries();
  const usableEntries: { [key: string]: string } = {};
  for (const [key, value] of entries) {
    usableEntries[key] = value;
  }
  return usableEntries;
}

function scrubUserInput(inputString: string): string {
  // escape the string before using it because it's from user input
  // this removes / - \ ^ $ * + ? . ( ) | [ ] { } from the string
  return inputString.replace(/[\/\-\\^$*+?.()|[\]{}]/g, '\\$&');
}

function areSetsEquivalent<T = string>(set1: Set<T>, set2: Set<T>): boolean {
  return set1.size === set2.size &&
    [...set1].every((item) => set2.has(item));
}

function generatePageTitle(newTitle: string): JSX.Element {
  return (
    <Helmet>
      <title>{`${newTitle} - Quest Analytics`}&reg;</title>
    </Helmet>
  );
}

// a succinct list of all focusable elements can be found here: https://html.spec.whatwg.org/dev/dom.html#interactive-content
// but this list should be fine for our purposes
const commonFocusableElements = "a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled])," +
  "input[type=\"text\"]:not([disabled]), input[type=\"radio\"]:not([disabled]), " +
  "input[type=\"checkbox\"]:not([disabled]), select:not([disabled]), input[type=\"file\"]:not([disabled])";

const focusFirstInteractiveElement = (ref?: RefObject<HTMLElement>): void => {
  const firstFocusableElement = ref?.current?.querySelector(commonFocusableElements) as HTMLElement;
  if (!!firstFocusableElement) {
    firstFocusableElement.focus();
  } else {
    // if there's nothing to focus on in the given element (or no ref), 
    // this should get the first thing in the app body
    (document.querySelector(".app-main").querySelector(commonFocusableElements) as HTMLElement)?.focus();
  }
}

const formatJobsFlyoutAsOf = (asOfToFormat: Date): string => {
  // we are not receiving the date with a UTC identifying 'Z', so we have to add it here for js to convert it properly
  const utcDate = new Date(asOfToFormat.toString() + "Z");
  return `${utcDate.toLocaleDateString()} at ${utcDate.toLocaleTimeString()}`;
}

const formatJobsFlyoutDuration = (durationToFormat: Date): string => {
  // split on periods to get rid of decimal seconds and other odd returns
  const holdDate = durationToFormat.toString().split('.');
  // as there will never be decimals of decimal seconds, we know that hours:minutes:seconds will always be in the second to last position
  return holdDate[holdDate.length - 2];
}

const isZipCodeInputValid = (value: string): boolean => {
  if (!/^\d{0,5}$/.test(value)) {
    return false;
  }
  return true;
}

const checkForEnter = (e: React.KeyboardEvent<HTMLInputElement>, callback: () => void): void => {
  if (e.key === "Enter") {
      // props.onSubmit(props.escapeSearchValue ? scrubUserInput(inputField.current.value) : inputField.current.value);
      callback();
  }
};

// Formats the given camelCase string to a human readable text, where the first character is capitalized and there is a space before the uppercase characters
const formatEnumValue = (value: string): string => {
  return value
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase());
};

export {
  allObjectKeysValid,
  areSetsEquivalent,
  capitalizeFirstLetter,
  checkForEnter,
  commonFocusableElements,
  focusFirstInteractiveElement,
  fromNullable,
  getComparisonChangeElement,
  getScrollBarWidth,
  preventDefaultMouseEvent,
  sortStringListOrderAscending,
  updateFieldNames,
  isNullOrUndefined,
  valueOrDashes,
  truncateStringWithEllipsis,
  querystringify,
  parseQuerystring,
  scrubUserInput,
  generatePageTitle,
  formatJobsFlyoutAsOf,
  formatJobsFlyoutDuration,
  isZipCodeInputValid,
  formatEnumValue
};